import React, { ElementType, FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, notification, Select, Tooltip, Form } from 'src/common';
import { Tag } from 'antd';
import OffsetAnchor from '../OffsetAnchor';
import styles from './index.module.scss';
import {
  useLoadBTags,
  useLoadMp,
  useUpdateMpBTags,
  getUpdateCacheOnUpdateMpBTags
} from 'src/models/matchingProfiles/hooks';
import { useForm } from 'antd/lib/form/Form';
import { CustomTagProps } from 'rc-select/es/BaseSelect';

interface IFormState {
  select: string[];
}

interface IProps {
  disabled: boolean;
}

export const CompanyTypes: FC<IProps> = props => {
  const { disabled } = props;
  const { t } = useTranslation();
  const [form] = useForm();

  const { data: bTagsData, loading: bTagsLoading } = useLoadBTags();
  const bTagsList = bTagsData?.getBTags;
  const { data: mp, loading: mpLoading } = useLoadMp();
  const selectedBTagsList = mp?.bTagIds;

  const [updateMpBTags] = useUpdateMpBTags();
  const formRef = useRef(null);
  const initialValues = useMemo(() => {
    return { select: selectedBTagsList || [] };
  }, [selectedBTagsList]);

  useEffect(() => {
    if (formRef.current) {
      !!form &&
        form.setFieldsValue({
          select: selectedBTagsList || []
        });
    }
  }, [form, selectedBTagsList]);

  const loading = bTagsLoading || mpLoading;

  const tagRender = useCallback((props: CustomTagProps) => {
    const { label, closable, onClose } = props;

    const [title, description] = (label?.toString().split(' ') ?? []).filter(
      substr => substr !== ' ' && substr !== '—'
    );
    const Component = (description ? Tooltip : 'div') as ElementType;
    return (
      <Component title={description}>
        <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
          {title}
        </Tag>
      </Component>
    );
  }, []);

  const onValuesChange = useCallback(
    (changedValues: Partial<IFormState>) => {
      if (!changedValues.select) {
        return;
      }
      const bTagIdsValue = changedValues.select;
      mp &&
        updateMpBTags({
          variables: {
            id: mp.id,
            bTagIds: bTagIdsValue
          },
          update: getUpdateCacheOnUpdateMpBTags(mp.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              bTagIds: bTagIdsValue
            }
          }
        }).catch(() => {
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [mp, t, updateMpBTags]
  );

  return (
    <Card
      title={
        <>
          <OffsetAnchor id={'companyTypes'} />
          {t('Tenders.BTags.industry')}
        </>
      }
      loading={loading}
      custom={true}
    >
      {bTagsList && (
        <Form
          form={form}
          className={styles.companyTypesContent}
          initialValues={initialValues}
          onValuesChange={onValuesChange}
          ref={formRef}
        >
          <Form.Item name={'select'} label={''} className={styles.formItem}>
            <Select
              disabled={disabled}
              mode={'multiple'}
              style={{ width: '100%' }}
              optionFilterProp={'label'}
              placeholder={t('Tenders.BTags.bTagsSelectPlaceholder')}
              tagRender={tagRender}
              showArrow={true}
            >
              {bTagsList.map(bTag => (
                <Select.Option label={bTag.name} key={bTag.id} value={bTag.id}>
                  {bTag.name}
                  {bTag.description ? ' — ' : ''}
                  {bTag.description ? bTag.description : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      )}
    </Card>
  );
};

export default CompanyTypes;
